<template>
  <DefaultPageContainer
    :cnc-ads-page="cncAdsPage"
    :breadcrumb-text="$t('search.title')"
  >

    <section class="section">
      <div class="section__head">
        <h1 class="h4">{{ $t('search.title') }}</h1>
      </div>
      <!-- ARTICLES -->
      <DefaultTwoColumnsContent right-container-class="section__right-col--mobile-hide">
        <template #left>

          <div class="section__search-wrapper">
            <form class="search search--search-page" method="GET" action="/" @submit.prevent="formSubmit">
              <input
                v-model="inputModel"
                type="text"
                name="q"
                placeholder="Zadejte hledaný výraz"
              >
              <button
                type="submit"
                class="search__btn"
                v-if="inputModel.length"
              >
                <DefaultIconsSearch/>
              </button>
            </form>

            <div class="results">
              <span v-if="fulltext.length > 0 && (articleStore.getFilteredArticlesCount() > 0 || articleStore.isLoading)" class="results__pretext">
                {{ $t('search.results_pretext') }} {{ fulltext }}
              </span>
              <div v-else-if="fulltext.length > 0 && !articleStore.getFilteredArticlesCount() && hpArticleStore.getFilteredArticlesCount()">
                <strong>
                  {{ $t('search.results_not-found-h') }}
                </strong>
                <br>
                {{ $t('search.results_not-found-p') }}
              </div>
              <span v-else-if="fulltext.length === 0">
                {{ $t('search.results_none') }}
              </span>
            </div>
          </div>

          <div v-if="!articleStore.getFilteredArticlesCount() && articleStore.isLoading " class="section__articles-container">
            <!-- Skeletons -->
            <div
              v-if="articleStore.isLoading"
              v-for="item in 10"
              :key="`skeleton_${item}`"
              class="article-wrapper"
            >
              <DefaultSkeletonsArticleCard
                :type="ArticleComponentType.SEARCH"
              />
            </div>
          </div>
          <div v-else-if="articleStore.getFilteredArticlesCount() > 0" class="section__articles-container">
            <!-- article list -->
            <DefaultArticlesListing
              :articles="articleStore.getOnlyFilteredArticles(0, (page * pageArticlesCount))"
              :ad-page="cncAdsPage"
              :articles-type="ArticleComponentType.SEARCH"
            />

            <!-- Loading NEXT page -->
            <div
              v-if="articleStore.isLoading"
              v-for="item in 10"
              :key="`skeleton_${item}`"
              class="article-wrapper"
            >
              <DefaultSkeletonsArticleCard
                :type="ArticleComponentType.SEARCH"
              />
            </div>
            <!-- Next page -->
            <div
              v-if="articleStore.getArticlesCount() >= articleStore.limit"
              class="button-more-container"
            >
              <DefaultArticlesButtonMore
                :label="$t('articles.button_more')"
                :disabled="articleStore.isLoading"
                @click="nextPage"
              />
            </div>
          </div>
          <div v-else-if="hpArticleStore.getFilteredArticlesCount() > 0 && !articleStore.getFilteredArticlesCount()" class="section__articles-container">
            <!-- article list -->
            <DefaultArticlesListing
              :articles="hpArticleStore.getOnlyFilteredArticles(0, (page * pageArticlesCount))"
              :ad-page="cncAdsPage"
            />

            <!-- Loading NEXT page -->
            <div
              v-if="hpArticleStore.isLoading"
              v-for="item in 10"
              :key="`hp_skeleton_${item}`"
              class="article-wrapper"
            >
              <DefaultSkeletonsArticleCard
                :type="ArticleComponentType.DEFAULT"
              />
            </div>
            <!-- Next page -->
            <div
              v-if="hpArticleStore.getFilteredArticlesCount() >= hpArticleStore.limit"
              class="button-more-container"
            >
              <DefaultArticlesButtonMore
                :label="$t('articles.button_more')"
                :disabled="hpArticleStore.isLoading"
                @click="hpNextPage"
              />
            </div>

          </div>
          <div v-else-if="!hpArticleStore.getFilteredArticlesCount() && !articleStore.getFilteredArticlesCount() && !articleStore.isLoading && !hpArticleStore.isLoading">
            <DefaultErrorsNoResultsFound />
          </div>
        </template>

        <template #right>
          <!-- CNC Ads -->
          <div class="advertisement-right-col h-full">
            <div class="sticky-sidebar-ad-wrapper">
              <DefaultAdsCncWrapper
                :position="UniFeAdsPosition.COLUMN_STICKY"
                :page="cncAdsPage"
              />
            </div>
          </div>
        </template>

      </DefaultTwoColumnsContent>
    </section>
  </DefaultPageContainer>
</template>

<script setup lang="ts">
import {ARTICLE_QUERY_TYPE, useArticlesStore} from "~/stores/useArticlesStore"
import {ArticleStoreType} from "~/libs/constants/ArticleStoreType"
import ArticleComponentType from "~/libs/constants/ArticleComponentType"
import {useI18n} from "vue-i18n"
import {CncAdsPage} from "~/libs/constants/CncAdsPage"
import {UniFeAdsPosition} from "~/libs/constants/UniFeAdsPosition"

const {t} = useI18n()
// i18n routing
definePageMeta({
  name: 'search',
})
false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    cs: '/hledej',
    en: '/search'
  }
})
// Set SEO meta
useSeoWrapper().setSeoMeta({
  title: t('search.title'),
})

const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()
const cncAdsPage = CncAdsPage.SEARCH

const { scrollToTop } = useAnchorScroll({
  toTop: {
    scrollOptions: {
      behavior: 'smooth',
      offsetTop: 0,
    }
  },
})

const articleStore = useArticlesStore(ArticleStoreType.SEARCH)
const hpArticleStore = useArticlesStore(ArticleStoreType.HOMEPAGE)

const pageArticlesCount = Number(config.public.searchPageArticlesCount || 17)
const pageSsrArticlesCount = Number(config.public.searchSsrArticlesCount || 6)

let page = ref(Number(route.query.page || 1))
let fulltext = ref(String(route.query.q || ''))

let inputModel = ref(fulltext.value)

const filters = {
  fulltext: fulltext.value
}

if (import.meta.client) {
  watch(
    () => route.query.q,
    () => {
      const newFulltext = ref(String(route.query.q || ''))
      if (newFulltext.value !== fulltext.value) {
        fulltext.value = newFulltext.value
        inputModel = ref(fulltext.value)
        filters.fulltext = fulltext.value
        page.value = 1
        loadData()
        setTimeout(() => {
          scrollToTop()
        }, 200)
      }
    },
  )
}

const setRoute = () => {
  if (import.meta.client) {
    router.push({
      query: {
        q: fulltext.value,
        page: page.value > 1 ? page.value : undefined
      },
    })
  }
}

// set query params and reload articles
const loadData = async () => {
  setRoute()

  try {
    articleStore.setLimit(page.value * pageArticlesCount)
    await articleStore.fetchArticles(filters, ARTICLE_QUERY_TYPE.PREVIEW)
    articleStore.fetchDetailsForArticles(ARTICLE_QUERY_TYPE.WITH_AUTHOR)
  } catch (e) {
    // TODO - show message to customer?
  }

  // nothing found, load HP articles
  if (!articleStore.getFilteredArticlesCount()) {
    //setRoute()
    try {
      hpArticleStore.setLimit(page.value * pageArticlesCount)
      await hpArticleStore.fetchArticles({}, ARTICLE_QUERY_TYPE.PREVIEW)
      if (import.meta.client) {
        articleStore.fetchDetailsForArticles(ARTICLE_QUERY_TYPE.WITH_AUTHOR)
      }
    } catch (e) {
      // TODO - show message to customer?
    }
  }
}

// fulltext search
const formSubmit = () => {
  page.value = 1
  filters.fulltext = inputModel.value
  fulltext.value = inputModel.value
  loadData()
  scrollToTop()
  // reload ads
  useCncAdsWrapper().changePageAds(cncAdsPage, {
    route: route
  })
}

// load next page - articles
const nextPage = () => {
  page.value = page.value + 1
  loadData()
}

if (import.meta.server) {
  // sever articles preload - preload pageSsrArticlesCount articles on server
  // setRoute()

  try {
    articleStore.setLimit(pageSsrArticlesCount)
    await articleStore.fetchArticles(filters, ARTICLE_QUERY_TYPE.PREVIEW)
  } catch (e) {
    // TODO - show message to customer?
  }

  if (!articleStore.getFilteredArticlesCount()) {
    try {
      hpArticleStore.setLimit(pageSsrArticlesCount)
      await hpArticleStore.fetchArticles({}, ARTICLE_QUERY_TYPE.PREVIEW)
    } catch (e) {
      // TODO - show message to customer?
    }
  }
}

const hpNextPage = async () => {
  page.value = page.value + 1
  setRoute()

  try {
    hpArticleStore.setLimit(page.value * pageArticlesCount)
    hpArticleStore.fetchArticles({}, ARTICLE_QUERY_TYPE.PREVIEW).then(() => {
      hpArticleStore.fetchDetailsForArticles(ARTICLE_QUERY_TYPE.WITH_AUTHOR)
    })
  } catch (e) {
    // TODO - show message to customer?
  }
}

/**
 * On mounted - load data
 */
onMounted(async () => {
  // client side data load
  await loadData()

  /**
   * CncAds Page change
   */
  useCncAdsWrapper().changePageAds(cncAdsPage, {
    route: route
  })
})
</script>